import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  LayoutDashboard, 
  Package, 
  MessageSquare, 
  CheckCircle, 
  XCircle,
  TrendingUp,
  LogOut,
  Mail,
  Phone,
  Building2,
  Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { logout, getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, [filter]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch stats
      const statsResponse = await axios.get(`${API}/admin/stats`, {
        headers: getAuthHeaders()
      });
      setStats(statsResponse.data);

      // Fetch inquiries
      const inquiriesUrl = filter === 'all' 
        ? `${API}/inquiries?limit=100`
        : `${API}/inquiries?status=${filter}&limit=100`;
        
      const inquiriesResponse = await axios.get(inquiriesUrl, {
        headers: getAuthHeaders()
      });
      setInquiries(inquiriesResponse.data);
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        logout();
        navigate('/admin/login');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (inquiryId, newStatus) => {
    try {
      await axios.patch(
        `${API}/inquiries/${inquiryId}`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      
      toast.success('Status updated successfully');
      fetchDashboardData();
      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry({ ...selectedInquiry, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleNotesUpdate = async (inquiryId, notes) => {
    try {
      await axios.patch(
        `${API}/inquiries/${inquiryId}`,
        { notes },
        { headers: getAuthHeaders() }
      );
      
      toast.success('Notes saved successfully');
      fetchDashboardData();
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'New': 'bg-blue-100 text-blue-800',
      'Contacted': 'bg-yellow-100 text-yellow-800',
      'Quoted': 'bg-purple-100 text-purple-800',
      'Closed': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="container-custom py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Package className="h-8 w-8 text-orange-500" />
              <div>
                <h1 className="text-2xl font-bold text-navy-900">Sagar Pack Admin</h1>
                <p className="text-sm text-gray-600">Inquiry Management Dashboard</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Welcome, <strong>{user?.username}</strong></span>
              <Button 
                variant="outline"
                onClick={handleLogout}
                className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container-custom py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <Card className="border-2 hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Inquiries</p>
                  <p className="text-3xl font-bold text-navy-900">{stats?.total_inquiries || 0}</p>
                </div>
                <LayoutDashboard className="h-10 w-10 text-blue-500 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">New</p>
                  <p className="text-3xl font-bold text-blue-600">{stats?.new_inquiries || 0}</p>
                </div>
                <MessageSquare className="h-10 w-10 text-blue-500 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Contacted</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats?.contacted_inquiries || 0}</p>
                </div>
                <CheckCircle className="h-10 w-10 text-yellow-500 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Quoted</p>
                  <p className="text-3xl font-bold text-purple-600">{stats?.quoted_inquiries || 0}</p>
                </div>
                <TrendingUp className="h-10 w-10 text-purple-500 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Closed</p>
                  <p className="text-3xl font-bold text-green-600">{stats?.closed_inquiries || 0}</p>
                </div>
                <XCircle className="h-10 w-10 text-green-500 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-3">
          {['all', 'New', 'Contacted', 'Quoted', 'Closed'].map((filterOption) => (
            <Button
              key={filterOption}
              variant={filter === filterOption ? 'default' : 'outline'}
              onClick={() => setFilter(filterOption)}
              className={filter === filterOption ? 'bg-orange-500 hover:bg-orange-600' : 'border-gray-300'}
            >
              {filterOption === 'all' ? 'All Inquiries' : filterOption}
            </Button>
          ))}
        </div>

        {/* Inquiries Table */}
        <Card className="border-2">
          <CardHeader className="border-b bg-slate-50">
            <h2 className="text-xl font-semibold text-navy-900">Inquiries</h2>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Product Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {inquiries.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        No inquiries found
                      </td>
                    </tr>
                  ) : (
                    inquiries.map((inquiry) => (
                      <tr 
                        key={inquiry.id} 
                        className="hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => setSelectedInquiry(inquiry)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {formatDate(inquiry.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-navy-900">{inquiry.name}</div>
                          <div className="text-sm text-gray-500">{inquiry.company || '-'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          <div className="flex items-center space-x-1">
                            <Mail className="h-3 w-3" />
                            <span>{inquiry.email}</span>
                          </div>
                          <div className="flex items-center space-x-1 mt-1">
                            <Phone className="h-3 w-3" />
                            <span>{inquiry.phone}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {inquiry.productType || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(inquiry.status)}`}>
                            {inquiry.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedInquiry(inquiry);
                            }}
                            className="text-orange-500 border-orange-500 hover:bg-orange-500 hover:text-white"
                          >
                            View Details
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inquiry Detail Modal */}
      {selectedInquiry && (
        <InquiryDetailModal
          inquiry={selectedInquiry}
          onClose={() => setSelectedInquiry(null)}
          onStatusUpdate={handleStatusUpdate}
          onNotesUpdate={handleNotesUpdate}
          getStatusColor={getStatusColor}
          formatDate={formatDate}
        />
      )}
    </div>
  );
};

// Inquiry Detail Modal Component
const InquiryDetailModal = ({ 
  inquiry, 
  onClose, 
  onStatusUpdate, 
  onNotesUpdate,
  getStatusColor,
  formatDate
}) => {
  const [notes, setNotes] = useState(inquiry.notes || '');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  const handleSaveNotes = async () => {
    setIsSavingNotes(true);
    await onNotesUpdate(inquiry.id, notes);
    setIsSavingNotes(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="border-b bg-slate-50 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-navy-900">Inquiry Details</h2>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Status Update */}
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-2 block">Update Status</Label>
            <div className="flex flex-wrap gap-2">
              {['New', 'Contacted', 'Quoted', 'Closed'].map((status) => (
                <Button
                  key={status}
                  variant={inquiry.status === status ? 'default' : 'outline'}
                  onClick={() => onStatusUpdate(inquiry.id, status)}
                  className={inquiry.status === status ? 'bg-orange-500 hover:bg-orange-600' : ''}
                  size="sm"
                >
                  {status}
                </Button>
              ))}
            </div>
          </div>

          {/* Contact Information */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700">Name</Label>
              <p className="mt-1 text-navy-900">{inquiry.name}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Company</Label>
              <p className="mt-1 text-navy-900">{inquiry.company || '-'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Email</Label>
              <p className="mt-1 text-navy-900">{inquiry.email}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Phone</Label>
              <p className="mt-1 text-navy-900">{inquiry.phone}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Product Type</Label>
              <p className="mt-1 text-navy-900">{inquiry.productType || '-'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Quantity</Label>
              <p className="mt-1 text-navy-900">{inquiry.quantity || '-'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Created At</Label>
              <p className="mt-1 text-navy-900">{formatDate(inquiry.createdAt)}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700">Status</Label>
              <span className={`mt-1 inline-block px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(inquiry.status)}`}>
                {inquiry.status}
              </span>
            </div>
          </div>

          {/* Message */}
          <div>
            <Label className="text-sm font-medium text-gray-700">Message</Label>
            <div className="mt-1 p-4 bg-slate-50 rounded-lg border border-gray-200">
              <p className="text-navy-900 whitespace-pre-wrap">{inquiry.message}</p>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-sm font-medium text-gray-700 mb-2 block">Admin Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add internal notes about this inquiry..."
              rows={4}
              className="border-gray-300 focus:border-orange-500 focus:ring-orange-500"
            />
            <Button
              onClick={handleSaveNotes}
              disabled={isSavingNotes}
              className="mt-2 bg-orange-500 hover:bg-orange-600"
            >
              {isSavingNotes ? 'Saving...' : 'Save Notes'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
